import useAuth from '@app/auth/use-auth';
import useLogout from '@app/auth/use-logout';
import useSwitchOrganisation from '@app/auth/use-switch-organisation';
import { groupRoutes } from '@app/group/group-routes';
import { Membership, OrganizationAccountTypeEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import { Avatar, AvatarFallback } from '@shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@shared/components/ui/sidebar';
import { capitalizeFirstLetter, cn } from '@shared/lib/utils';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { orgIcon } from './org-icon';
import { OrgSearch } from './org-search';

type OrgGroup = { parent: Membership; children: Membership[] };

const groupMemberships = (memberships: readonly Membership[]) => {
  const sorted = [...memberships].sort((a, b) => a.name.localeCompare(b.name));
  const parents = sorted.filter(
    (m) => m.accountType === OrganizationAccountTypeEnum.parent
  );
  const groups: OrgGroup[] = parents.map((parent) => ({
    parent,
    children: sorted.filter((m) => m.groupUuid === parent.uuid),
  }));
  const claimed = new Set(
    groups.flatMap((g) => [g.parent.uuid, ...g.children.map((c) => c.uuid)])
  );
  const others = sorted.filter((m) => !claimed.has(m.uuid));
  return { groups, others };
};

type OrgMenuItemProps = {
  membership: Membership;
  current: boolean;
  indent?: boolean;
  disabled: boolean;
  onSelect: () => void;
};

const OrgMenuItem = ({
  membership,
  current,
  indent,
  disabled,
  onSelect,
}: OrgMenuItemProps) => {
  // Indented rows are children of the parent above; a branch glyph signals
  // that relationship and lets us drop the visual weight a notch vs the
  // standalone-row icons.
  const Icon = indent ? Icons.dot : orgIcon(membership.accountType);
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={(event) => {
        if (current) {
          event.preventDefault();
          return;
        }
        onSelect();
      }}
      className={cn(
        'gap-2',
        indent && 'pl-2',
        current &&
          'bg-primary-50 text-primary-700 focus:bg-primary-50 focus:text-primary-700'
      )}
    >
      <Icon
        className={cn(
          'size-4 shrink-0',
          indent ? 'text-gray-400' : 'text-gray-500'
        )}
      />
      <span className="truncate">{membership.name}</span>
    </DropdownMenuItem>
  );
};

export function NavUser() {
  const { user } = useAuth();
  const { switchOrganisation, isLoading } = useSwitchOrganisation();
  const logout = useLogout();

  const isStaff = !!user?.isStaff;
  const staffUserOnClientOrg =
    isStaff &&
    user?.organizationAccountType !== OrganizationAccountTypeEnum.staff;
  const parentOrg =
    user?.organizationAccountType === OrganizationAccountTypeEnum.parent;
  const settingsLink = parentOrg ? groupRoutes.GROUP_SETTINGS : '/settings';

  const { groups, others } = groupMemberships(user?.memberships ?? []);
  const hasOrgsToShow = groups.length > 0 || others.length > 0;

  const button = (
    <SidebarMenuButton
      size="lg"
      className={cn(
        'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground',
        {
          'bg-warning-100': staffUserOnClientOrg,
        }
      )}
    >
      <Avatar className="size-8 rounded-lg grayscale">
        <AvatarFallback className="rounded-lg">
          {user?.userName
            ? capitalizeFirstLetter(user?.userName[0])
            : capitalizeFirstLetter(user?.organizationName[0] as string)}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">{user?.userName}</span>
        <span className="truncate text-xs text-muted-foreground">
          {user?.organizationName}
        </span>
      </div>
    </SidebarMenuButton>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem className="px-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={isLoading}>
            {button}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
          >
            {isStaff ? (
              <>
                <DropdownMenuLabel>Switch organisation</DropdownMenuLabel>
                <OrgSearch
                  disabled={isLoading}
                  onSelect={(organisationUuid) =>
                    switchOrganisation(organisationUuid)
                  }
                />
              </>
            ) : (
              <>
                {hasOrgsToShow && (
                  <DropdownMenuLabel>Switch organisation</DropdownMenuLabel>
                )}
                {groups.map(({ parent, children }, groupIndex) => (
                  <Fragment key={parent.uuid}>
                    {groupIndex > 0 && <DropdownMenuSeparator />}
                    <OrgMenuItem
                      membership={parent}
                      current={parent.uuid === user?.organizationUuid}
                      disabled={isLoading}
                      onSelect={() => switchOrganisation(parent.uuid)}
                    />
                    {children.length > 0 && (
                      <DropdownMenuSeparator className="mb-0" />
                    )}
                    <div className="-mx-1 max-h-80 overflow-y-auto px-1">
                      {children.map((child) => (
                        <OrgMenuItem
                          key={child.uuid}
                          membership={child}
                          indent
                          current={child.uuid === user?.organizationUuid}
                          disabled={isLoading}
                          onSelect={() => switchOrganisation(child.uuid)}
                        />
                      ))}
                    </div>
                  </Fragment>
                ))}
                {others.length > 0 && groups.length > 0 && (
                  <DropdownMenuSeparator />
                )}
                {others.map((membership) => (
                  <OrgMenuItem
                    key={membership.uuid}
                    membership={membership}
                    current={membership.uuid === user?.organizationUuid}
                    disabled={isLoading}
                    onSelect={() => switchOrganisation(membership.uuid)}
                  />
                ))}
              </>
            )}
            {(isStaff || hasOrgsToShow) && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild>
              <Link to={settingsLink} className="gap-2">
                <Icons.settings className="size-4 shrink-0 text-gray-500" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => logout()} className="gap-2">
              <Icons.logOut className="size-4 shrink-0 text-gray-500" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
